<script setup lang="ts">
import { logError } from '~/utils/log-error'
import type { SpecialIssue } from '@/models/content'
import { useGoogleAnalyticsPageView } from '~/composables/google-analytics'
import { useMembershipStore } from '@/stores/membership'
import { useOpenPDFViewer } from '~~/composables/open-pdf-viewer'
import { useAuthStore } from '@/stores/auth'
import {
  makeTwitterSharableLink,
  makeLinkedinSharableLink,
} from '@/utils/string'

const membershipStore = useMembershipStore()
const authStore = useAuthStore()
const { callToAction, isDownloading, downloadProgress, cancelDownload } =
  useDownloadFile({ module: MODULES.SPECIAL_ISSUES })

const { $restClient, $seoManager, $modal, $auth, $cloudinary, $rudderstack } =
  useNuxtApp()
const { openPDFViewer } = useOpenPDFViewer()

const specialIssues = ref<SpecialIssue[]>()
const firstSpecialIssue = ref<SpecialIssue>()
const url = ref<string>(null)
const loading = ref(true)
const twitterUrl = ref<string>(null)
const linkedinUrl = ref<string>(null)

const seo = $seoManager.generateSeoInfo({
  title: 'Special Issues',
})
useHead(seo)
useGoogleAnalyticsPageView(seo.title)

const isAuthorized = computed(
  () =>
    membershipStore.isSubscriberActive ||
    membershipStore.isSpecialIssuesSubActive,
)

const fetchContent = async () => {
  try {
    const { data } = await $restClient.getSpecialIssues()
    specialIssues.value = data
    firstSpecialIssue.value = specialIssues.value.shift()
    url.value = `${window.location.href}/${firstSpecialIssue.value.id}`
    twitterUrl.value = makeTwitterSharableLink(
      url.value,
      firstSpecialIssue.value.title,
    )
    linkedinUrl.value = makeLinkedinSharableLink(
      url.value,
      firstSpecialIssue.value.id,
    )
  } catch (error) {
    logError(error)
  }

  loading.value = false
}

onMounted(async () => {
  await fetchContent()
})

function download() {
  callToAction.value.callback(
    firstSpecialIssue.value.id,
    'special-issues',
    firstSpecialIssue.value.fileName,
    firstSpecialIssue.value.subscriptionRequired,
  )
}

const selectedFormId = ref<number>(null)

const openCreateAccountModal = () => {
  $modal.currentModule = MODULES.SPECIAL_ISSUES
  $modal.open('createAccount')
  $rudderstack.trackContentGatedInteraction()
}
</script>

<template>
  <div class="flex grow flex-col">
    <LoadingSpinner
      v-if="$auth.store.token === undefined || loading"
      wrap-screen-with-submenu
    />

    <ClientOnly v-else>
      <div v-if="!loading" class="pb-20 lg:pb-36">
        <header
          class="relative bg-s-900 px-6 pb-5 pt-7 text-white md:px-5 md:py-6"
        >
          <div
            class="mx-auto flex max-w-[913px] flex-col"
            data-testid="featured-special-issue"
          >
            <span class="hidden text-2xl font-bold text-white md:block">
              Latest
            </span>
            <div
              class="flex justify-between gap-4 md:items-center md:gap-8 md:px-6 md:py-5"
            >
              <div
                class="hidden h-full shrink-0 bg-transparent md:block md:w-[340px]"
              >
                <img
                  v-if="firstSpecialIssue"
                  width="392"
                  height="195"
                  :src="
                    $cloudinary.resize(
                      firstSpecialIssue.coverUrl,
                      'special-issues-card',
                    )
                  "
                  alt=""
                  class="h-full w-full object-cover object-center"
                />
              </div>

              <div v-if="firstSpecialIssue" class="grow">
                <div class="flex flex-col justify-between md:flex-row">
                  <h1 class="font-bold" data-testid="special-issue-card-title">
                    {{ firstSpecialIssue.title }}
                  </h1>
                  <p class="mb-5 text-sm font-semibold md:hidden">
                    {{ firstSpecialIssue.publishedDate }}
                  </p>
                  <div
                    class="flex h-full w-full shrink-0 justify-center bg-transparent md:hidden"
                  >
                    <img
                      v-if="firstSpecialIssue"
                      width="274"
                      height="136"
                      :src="
                        $cloudinary.resize(
                          firstSpecialIssue.coverUrl,
                          'special-issues-card',
                        )
                      "
                      alt=""
                      class="h-full w-[274px] object-cover object-center"
                    />
                  </div>
                  <div class="hidden items-center justify-start gap-1 md:flex">
                    <p class="text-xs">Share</p>
                    <TooltipShare
                      variant="light"
                      placement="top"
                      :twitter-link="twitterUrl"
                      :linkedin-link="linkedinUrl"
                      :link="url"
                    />
                  </div>
                </div>
                <p class="hidden text-sm md:block">
                  {{ firstSpecialIssue.publishedDate }}
                </p>
                <div
                  class="mt-3 text-sm md:mt-2"
                  data-testid="special-issue-card-summary"
                  v-html="firstSpecialIssue.summary"
                ></div>
                <div
                  class="mt-7 flex items-center justify-between"
                  :class="[
                    firstSpecialIssue.tags.length
                      ? 'md:justify-between'
                      : 'md:justify-end',
                  ]"
                >
                  <div
                    v-if="firstSpecialIssue.tags.length"
                    class="hidden flex-wrap gap-2 md:flex"
                  >
                    <LinkedPill
                      v-for="tag in firstSpecialIssue.tags"
                      :key="tag.id"
                      :slug="tag.slug"
                      variant="black"
                    >
                      {{ tag.name }}
                    </LinkedPill>
                  </div>
                  <div class="flex items-center justify-start gap-1 md:hidden">
                    <p class="text-xs">Share</p>
                    <TooltipShare
                      variant="light"
                      placement="top"
                      :twitter-link="twitterUrl"
                      :linkedin-link="linkedinUrl"
                      :link="url"
                    />
                  </div>
                  <div
                    class="flex shrink-0 grow items-center justify-end gap-4"
                  >
                    <BaseButton
                      v-if="firstSpecialIssue.linkFlipHTML5 && isAuthorized"
                      variant="primary"
                      class="w-full max-w-[140px] shrink-0 grow md:max-w-[110px] md:grow-0"
                      data-testid="view-pdf"
                      @click="
                        openPDFViewer(
                          firstSpecialIssue.id,
                          firstSpecialIssue.title,
                          firstSpecialIssue.linkFlipHTML5,
                          'specialIssue',
                          firstSpecialIssue.subscriptionRequired,
                        )
                      "
                    >
                      Read Now
                    </BaseButton>
                    <div v-if="authStore.isAuthenticated" class="shrink-0">
                      <CircularProgressBar
                        v-if="isDownloading || downloadProgress"
                        :progress="downloadProgress"
                        :cancel-call-back="cancelDownload"
                        :is-loading="isDownloading"
                        @click="download"
                      />
                      <BaseButton
                        v-else
                        variant="tertiary"
                        class="grow bg-white md:grow-0"
                        data-testid="call-to-action"
                        @click="download"
                      >
                        {{
                          callToAction.text === 'Subscribe'
                            ? 'Download Report'
                            : callToAction.text
                        }}
                      </BaseButton>
                    </div>
                    <div v-else class="flex w-full items-center justify-end">
                      <BaseButton
                        variant="tertiary"
                        class="max-h-fit shrink-0 bg-white md:grow-0"
                        data-testid="call-to-action"
                        @click="openCreateAccountModal"
                      >
                        Download Report
                      </BaseButton>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </header>

        <section
          class="mx-2 mt-5 items-start gap-5 lg:mx-auto lg:mt-10 lg:flex lg:max-w-[913px]"
        >
          <div class="flex flex-col gap-5 md:gap-3">
            <SpecialIssueCard
              v-for="specialIssue in specialIssues"
              :id="specialIssue.id"
              :key="specialIssue.id"
              :title="specialIssue.title"
              :form-id="specialIssue.formId"
              :summary="specialIssue.summary"
              :pills="specialIssue.tags"
              :cover-url="specialIssue.coverUrl"
              :file-name="specialIssue.fileName"
              :publish-date="specialIssue.publishedDate"
              :link-flip-html5="specialIssue.linkFlipHTML5"
              :subscription-required="specialIssue.subscriptionRequired"
              :special-issue-data="specialIssue"
              @open-modal="openCreateAccountModal"
            />
          </div>
        </section>
      </div>
    </ClientOnly>
    <BaseModal
      name="downloadSpecialIssue"
      data-testid="special-issue-modal"
      bg-white
    >
      <DownloadSpecialIssueForm module="modal" :form-id="selectedFormId" />
    </BaseModal>
  </div>
</template>
